/**
 * The settings panel: what can be changed, what cannot, and what is in force.
 *
 * Split out of `AdminScreen.tsx` for the same reason `EvaluationTab.tsx` was —
 * the screen was already long, and this is a self-contained job with its own
 * vocabulary.
 *
 * The design decision worth knowing before reading the markup: every row shows
 * three layers, not one. The code default, what this deployment's environment
 * configured, and the override an administrator wrote. Showing a single number
 * would make the screen lie — this deployment runs on six passages against a
 * code default of eighteen, so "the default is 18" is exactly the wrong thing
 * to tell the person deciding whether to change it.
 *
 * The second decision: a setting that cannot really be changed is shown here
 * anyway, with the reason and the remedy, and has no control at all. That is
 * half the point of the screen. "Where do I change the model?" now has an
 * answer, and the answer is not a switch attached to nothing.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  api,
  type SettingGroup,
  type SettingRow as Row,
  type SettingsResponse,
} from "../../lib/api";
import { timeAgo } from "../../lib/format";
import {
  Card,
  ErrorNote,
  InfoDot,
  Spinner,
  Toggle,
  useToast,
} from "../../components/ui";

/**
 * Feather-shaped, hand-rolled, same geometry as the sidebar's icons. There is
 * no icon library in this application and adding one for eight glyphs would be
 * a dependency per glyph. `NavIcon` is not exported and takes two paths at
 * most, so this is its unrestricted sibling rather than a change to it.
 */
function GroupIcon({ paths, size = 15 }: { paths: string[]; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="shrink-0"
      aria-hidden
    >
      {paths.map((d, index) => (
        <path key={index} d={d} />
      ))}
    </svg>
  );
}

const GROUP_ICON: Record<string, string[]> = {
  // A magnifier over stacked results.
  retrieval: [
    "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14",
    "m16.2 16.2 4 4",
    "M8.5 9h5",
    "M8.5 12.5h3",
  ],
  // A four-point spark.
  generation: [
    "M12 3.2 13.8 9l5.8 1.8-5.8 1.8L12 18.4l-1.8-5.8L4.4 10.8 10.2 9z",
    "M18.8 4v3",
    "M17.3 5.5h3",
  ],
  // A loop-back arrow over a speech bubble.
  followup: [
    "M4 9.5h11a4 4 0 0 1 0 8h-3.5",
    "m7.5 6-3.5 3.5L7.5 13",
    "M20 6.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0",
  ],
  // A layered stack.
  chunking: ["m12 3 9 4.3-9 4.3-9-4.3z", "m3 12 9 4.3 9-4.3", "m3 16.4 9 4.3 9-4.3"],
  // A shield with a tick.
  quality: ["M12 3 5 6v5.6c0 4.3 3 8.1 7 9.4 4-1.3 7-5.1 7-9.4V6z", "m9 12 2.2 2.2L15.2 10"],
  // A balance scale.
  judge: ["M12 4v16", "M6 20h12", "M4 8h16", "m4 8-2.5 5a3 3 0 0 0 5 0z", "m20 8-2.5 5a3 3 0 0 0 5 0z"],
  // A page with a clock.
  documents: [
    "M6 3h8l4 4v9",
    "M14 3v4h4",
    "M6 3v14",
    "M13 18.5a4 4 0 1 0 8 0 4 4 0 0 0-8 0",
    "M17 16.8v1.7l1.2.9",
  ],
  // A padlock over a chip.
  platform: ["M8 10.5V8a4 4 0 0 1 8 0v2.5", "M5.5 10.5h13V20h-13z", "M12 14v2.5"],
};

const RESET_ICON = ["M3.5 12a8.5 8.5 0 1 0 2.9-6.4L3 8", "M3 3.5V8h4.5"];
const LOCK_ICON = ["M8.5 10.5V8.2a3.5 3.5 0 0 1 7 0v2.3", "M6.5 10.5h11V19h-11z"];

const APPLIES_LABEL: Record<string, string> = {
  immediately: "applies immediately",
  next_question: "applies to the next question",
  next_answer: "applies to the next answer",
  next_ingestion: "applies to the next ingestion",
  restart: "needs a restart",
};

const show = (value: number | boolean | string) =>
  typeof value === "boolean" ? (value ? "on" : "off") : String(value);

/**
 * A number held locally until it is committed.
 *
 * Committed on blur or Enter, abandoned on Escape. Mutating on every keystroke
 * would fire a request and write an audit row per digit — typing 1200 would
 * leave a trail reading 1, 12, 120, 1200, three quarters of which nobody chose.
 */
function NumberField({
  row,
  disabled,
  onCommit,
}: {
  row: Row;
  disabled: boolean;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState(String(row.value));
  useEffect(() => setDraft(String(row.value)), [row.value]);

  const commit = () => {
    const parsed = Number(draft);
    if (draft.trim() === "" || Number.isNaN(parsed)) {
      setDraft(String(row.value));
      return;
    }
    if (parsed === Number(row.value)) return;
    onCommit(parsed);
  };

  return (
    <input
      type="number"
      className="field-toolbar w-[96px] text-right tabular-nums"
      value={draft}
      disabled={disabled}
      min={row.minimum ?? undefined}
      max={row.maximum ?? undefined}
      step={row.step ?? undefined}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          setDraft(String(row.value));
          event.currentTarget.blur();
        }
      }}
    />
  );
}

function SettingLine({
  row,
  busy,
  onChange,
  onReset,
}: {
  row: Row;
  busy: boolean;
  onChange: (value: number | boolean | string) => void;
  onReset: () => void;
}) {
  return (
    <li className="border-b border-line-soft px-4 py-3 last:border-0">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {row.locked && (
              <span className="text-muted-soft" title="Read-only">
                <GroupIcon paths={LOCK_ICON} size={12} />
              </span>
            )}
            <span className="text-base+ font-bold text-ink">{row.label}</span>
            {(row.rationale || row.locked) && (
              <InfoDot title={row.label} text={row.locked ?? row.rationale ?? ""} />
            )}
          </div>
          <p className="mt-0.5 text-sm leading-[1.5] text-muted">{row.detail}</p>
          <Provenance row={row} onReset={onReset} busy={busy} />
        </div>

        <div className="flex shrink-0 items-center gap-2 pt-0.5">
          {row.locked ? (
            // Stated, not disabled. A greyed-out control reads as broken; no
            // control at all reads as "this is not a thing you set here",
            // which is the truth.
            <span className="text-base+ font-bold text-ink">{show(row.value)}</span>
          ) : row.kind === "bool" ? (
            <Toggle
              checked={Boolean(row.value)}
              disabled={busy}
              label={row.label}
              onChange={(next) => onChange(next)}
            />
          ) : (
            <NumberField row={row} disabled={busy} onCommit={onChange} />
          )}
          {row.unit && !row.locked && (
            <span className="w-[62px] text-2xs text-muted-soft">{row.unit}</span>
          )}
        </div>
      </div>
    </li>
  );
}

/** Which of the three layers this row is currently sitting on. */
function Provenance({
  row,
  busy,
  onReset,
}: {
  row: Row;
  busy: boolean;
  onReset: () => void;
}) {
  if (row.isOverridden) {
    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-muted-soft">
        <span
          className="rounded-[4px] px-1.5 py-px font-extrabold"
          style={{ background: "#E6F7DC", color: "#2F6420" }}
        >
          Changed
        </span>
        <span>configured {show(row.configured)}</span>
        {row.updatedBy && row.updatedAt && (
          <span>
            · {row.updatedBy}, {timeAgo(row.updatedAt)}
          </span>
        )}
        <button
          className="btn-bare inline-flex items-center gap-1 text-2xs"
          disabled={busy}
          onClick={onReset}
        >
          <GroupIcon paths={RESET_ICON} size={11} />
          Reset
        </button>
      </div>
    );
  }
  if (row.configuredFromEnvironment && !row.locked) {
    // The whole reason this screen shows three layers. Without this line an
    // administrator reads "18" off the code and cannot tell that their system
    // has been answering on six since it was deployed.
    return (
      <div className="mt-1.5 text-2xs text-muted-soft">
        set in the environment · code default {show(row.codeDefault)}
      </div>
    );
  }
  return null;
}

function Group({
  group,
  busy,
  onChange,
  onReset,
}: {
  group: SettingGroup;
  busy: string | null;
  onChange: (row: Row, value: number | boolean | string) => void;
  onReset: (row: Row) => void;
}) {
  const applies = group.settings[0]?.appliesTo;
  return (
    <Card
      className="mt-4 first:mt-0"
      title={
        <span className="flex items-center gap-[7px]">
          <span className="text-muted">
            <GroupIcon paths={GROUP_ICON[group.key] ?? []} />
          </span>
          <span>{group.label}</span>
        </span>
      }
      meta={
        <span className="text-2xs text-muted-soft">
          {group.settings.every((s) => s.locked)
            ? "read-only"
            : (applies && APPLIES_LABEL[applies]) || ""}
        </span>
      }
      collapsible
      storageKey={`settings-${group.key}`}
      defaultOpen={group.key === "retrieval"}
      padded={false}
    >
      <p className="border-b border-line-soft px-4 py-2 text-2xs text-muted">
        {group.detail}
      </p>
      <ul>
        {group.settings.map((row) => (
          <SettingLine
            key={row.key}
            row={row}
            busy={busy === row.key}
            onChange={(value) => onChange(row, value)}
            onReset={() => onReset(row)}
          />
        ))}
      </ul>
      {group.footer && (
        <p className="border-t border-line-soft px-4 py-2.5 text-2xs leading-[1.55] text-muted-soft">
          {group.footer}
        </p>
      )}
    </Card>
  );
}

export function Settings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<SettingsResponse>("/admin/settings"),
  });

  const settled = (message: string) => {
    setBusy(null);
    toast(message);
    void queryClient.invalidateQueries({ queryKey: ["settings"] });
    // The assistant's own Answer settings panel reads the passage budget and
    // the relevance floor from /version, which is cached for the session. It
    // would otherwise go on reporting the number this screen just changed.
    void queryClient.invalidateQueries({ queryKey: ["version"] });
  };

  const failed = (caught: unknown) => {
    setBusy(null);
    toast(caught instanceof Error ? caught.message : "That change was refused", "danger");
    // Put the control back to what the server says it is: a refused change
    // must not leave the screen showing a value nothing is using.
    void queryClient.invalidateQueries({ queryKey: ["settings"] });
  };

  const update = useMutation({
    mutationFn: ({ row, value }: { row: Row; value: number | boolean | string }) =>
      api.patch<Row>(`/admin/settings/${row.key}`, { value }),
    onMutate: ({ row }) => setBusy(row.key),
    onSuccess: (updated) => settled(`${updated.label} is now ${show(updated.value)}`),
    onError: failed,
  });

  const reset = useMutation({
    mutationFn: (row: Row) => api.delete<Row>(`/admin/settings/${row.key}`),
    onMutate: (row: Row) => setBusy(row.key),
    onSuccess: (updated) =>
      settled(`${updated.label} is back to ${show(updated.value)}`),
    onError: failed,
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <>
      {data.groups.map((group) => (
        <Group
          key={group.key}
          group={group}
          busy={busy}
          onChange={(row, value) => update.mutate({ row, value })}
          onReset={(row) => reset.mutate(row)}
        />
      ))}
      <p className="note-dashed mt-4">
        Changes are written to the database and read at the point of use, so
        they apply without a restart. The worker containers pick them up within{" "}
        {data.propagationSeconds} seconds; work already running finishes on the
        values it started with.
      </p>
    </>
  );
}
